import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Card, Select, Skeleton, Tag, Typography, message } from "antd";

import {
  clientAdvisorModelOptions,
  getClientAdvisorModelOption,
  type ClientAdvisorConfigData,
  type ClientAdvisorModelOptionId,
} from "@fitness-app/data-models/entities/ClientAdvisorConfig";
import { AppConfigType, type ClientAdvisorAppConfig } from "@fitness-app/data-models/entities/TrainerConfig";
import { type Json } from "@fitness-app/supabase";

import { supabase } from "~/store/initializeStore";

// TRE-1613 — the advisor model is configured here (app_config, type client_advisor) and
// resolved server-side by every entry point: live chat, proactive messages and
// admin-initiated conversations. Update is RLS-gated to the account owner (TRAINER).

const queryKey = ["app_config", AppConfigType.CLIENT_ADVISOR];

export const AdvisorModelCard = (): React.ReactElement => {
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select()
        .eq("type", AppConfigType.CLIENT_ADVISOR)
        .maybeSingle<ClientAdvisorAppConfig>();

      if (error) throw error;

      return data;
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: async (patch: Partial<ClientAdvisorConfigData>) => {
      const config = configQuery.data;
      if (!config?.id) {
        throw new Error("Brak rekordu konfiguracji Opiekunki (app_config, typ client_advisor).");
      }

      // Optimistic concurrency: the version predicate rejects a write based on a stale
      // snapshot (model Select and report Switch patch different fields of one row).
      const { data, error } = await supabase
        .from("app_config")
        .update({
          // Wycofane id modelu normalizujemy przy każdym zapisie. Inaczej zmiana
          // samego przełącznika raportu utrwalałaby id, którego nie ma już na
          // liście — a doradca i tak chodziłby wtedy na modelu domyślnym.
          data: {
            ...config.data,
            modelOptionId: getClientAdvisorModelOption(config.data.modelOptionId).id,
            ...patch,
          } as unknown as Json,
          updatedAt: new Date().toISOString(),
          version: (config.version ?? 0) + 1,
        })
        .eq("id", config.id)
        .eq("version", config.version ?? 0)
        .select()
        .maybeSingle<ClientAdvisorAppConfig>();

      if (error) throw error;
      if (!data) {
        await queryClient.invalidateQueries({ queryKey });
        throw new Error("Konfiguracja została zmieniona w innym miejscu — odśwież i spróbuj ponownie.");
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      void message.success("Konfiguracja Opiekunki zapisana.");
    },
    onError: (error) => {
      void message.error(
        error instanceof Error
          ? error.message
          : "Nie udało się zapisać konfiguracji (zmiana wymaga konta właściciela).",
      );
    },
  });

  return (
    <Card size="small" title="Konfiguracja Opiekunki" style={{ marginBottom: 16 }}>
      {configQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : configQuery.isError || !configQuery.data ? (
        <Alert
          type="warning"
          showIcon
          message="Brak konfiguracji Opiekunki"
          description="Nie znaleziono rekordu app_config typu client_advisor — do czasu jego dodania Opiekunka działa na modelu domyślnym."
        />
      ) : (
        <>
          <Typography.Text strong>Model</Typography.Text>
          <div style={{ marginTop: 8 }}>
            <Select<ClientAdvisorModelOptionId>
              style={{ width: 320 }}
              // To, na czym doradca realnie leci: przy wycofanym id backend też
              // wpada na domyślny, więc picker ma pokazywać to samo, a nie pustkę.
              value={getClientAdvisorModelOption(configQuery.data.data.modelOptionId).id}
              loading={updateConfigMutation.isLoading}
              disabled={updateConfigMutation.isLoading}
              onChange={(modelOptionId) => updateConfigMutation.mutate({ modelOptionId })}
              options={clientAdvisorModelOptions.map((option) => ({
                value: option.id,
                label: (
                  <span>
                    {option.label}
                    {/* Poziom rozumowania ma tylko OpenAI — przy Gemini pusty tag
                        wyglądał jak brakująca wartość. */}
                    {option.reasoningEffort ? <Tag style={{ marginLeft: 4 }}>{option.reasoningEffort}</Tag> : null}
                  </span>
                ),
              }))}
            />
          </div>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 8 }}>
            Obowiązuje wszędzie: czat w aplikacji, wiadomości proaktywne i rozmowy otwierane z admina. Zmiana zapisuje
            się od razu; zmienić może tylko konto właściciela. Raport AI dla klientki planujesz jako zadanie
            automatyzacji programu („Dodaj raport AI dla klientki") — model raportu ustawisz w Konfiguracji aplikacji.
          </Typography.Paragraph>
        </>
      )}
    </Card>
  );
};
